import React, { type RefObject, useCallback, useRef, useState } from 'react';
import { isNil } from 'lodash';

import { getMessageId } from '../../conversation/metadata';
import type { LogPanelConversation, QueuedMessage, SendingMessage } from '../../conversation/types';
import Composer from '../Composer';
import { useComposerSend } from '../Composer/useComposerSend';
import { useDissolveQueueItem } from '../hooks/useDissolveQueueItem';
import QueueStrip, { type QueueRow } from '../QueueStrip';
import type { LogEntryModel } from '../types';

/** The entry of each handler's message the log holds, by the message's id. */
const turnEventIdsOf = (entries: LogEntryModel[]): Map<string, string> =>
  new Map(
    entries.flatMap((entry): [string, string][] => {
      const messageId = entry.kind === 'handler_turn' ? getMessageId(entry.notification) : null;

      return messageId ? [[messageId, entry.notification.event_id]] : [];
    })
  );

/** A message sent from here, with the queue as it was read when the server took it. */
interface SentRow extends QueueRow {
  readUnder: QueuedMessage[];
}

interface ConversationFootProps {
  conversation: LogPanelConversation;
  /** Every entry of the log, whatever the reader filtered it to. */
  entries: LogEntryModel[];
  /** The log's scrolling box, which a message coming in from the queue may keep at its newest end. */
  scrollerRef: RefObject<HTMLElement>;
  isReversed: boolean;
  /** Whether the log keeps the reader at its newest end as it draws more, where a message coming in from the queue keeps them too. */
  shouldFollow: () => boolean;
}

/**
 * Pinned under the log: the messages Clive has not read yet, then the composer. A message the handler sends waits,
 * greyed, as the queue's last row until the server has it. A message leaves the queue once the log holds its entry,
 * and moves into it.
 */
const ConversationFoot: React.FC<ConversationFootProps> = ({
  conversation,
  entries,
  scrollerRef,
  isReversed,
  shouldFollow,
}) => {
  const { queue, send } = conversation;
  // The handler's own messages, which the log may move to show as it takes them in.
  const sentHereRef = useRef(new Set<string>());
  // The queue as last read, for a sent row to tell a later read from the one it was sent under.
  const queueRef = useRef(queue);
  queueRef.current = queue;
  // Sent from here: each drawn as queued, with its id, until the queue or the log holds it, or a later read leaves
  // it out, so a message whose read after the send failed does not go from the queue until a read has it.
  const [sentRows, setSentRows] = useState<SentRow[]>([]);
  const sendHere = useCallback(
    async ({ text, sentAt }: SendingMessage) => {
      const id = await send(text);
      sentHereRef.current.add(id);
      setSentRows((rows) => [...rows, { id, created_at: sentAt, text, readUnder: queueRef.current }]);
    },
    [send]
  );
  const { inFlight, ...composer } = useComposerSend(sendHere);
  const turnEventIds = turnEventIdsOf(entries);
  const itemRef = useDissolveQueueItem(scrollerRef, turnEventIds, isReversed, sentHereRef.current, shouldFollow);
  const listedIds = new Set(queue.map(({ id }) => id));
  // A read after the send that leaves the message out lists only open messages: a run has closed it.
  const unlisted = sentRows.filter(
    ({ id, readUnder }) => readUnder === queue && !listedIds.has(id) && !turnEventIds.has(id)
  );
  // Once listed, in the log or closed, a row is theirs to draw, or to leave out once read.
  if (unlisted.length < sentRows.length) setSentRows(unlisted);
  // Unread until a run is told it: `told_at` is null until then.
  const unread = queue.filter(({ id, told_at: toldAt }) => isNil(toldAt) && !turnEventIds.has(id));

  return (
    // The log's column: its inset, and the gutter it keeps for its scrollbar, so the composer's edges are the
    // entries'. `overflow-hidden` makes the foot a scroll container, the only box `scrollbar-gutter` applies to.
    <div className="shrink-0 overflow-hidden px-24 [scrollbar-gutter:stable]">
      <QueueStrip messages={unread} sent={unlisted} sending={inFlight} itemRef={itemRef} />
      <Composer {...composer} />
    </div>
  );
};

export default ConversationFoot;
